import { fromBase64, randomBytes, toBase64, utf8 } from './encoding';

/**
 * 암호문 봉투. AES-GCM-256, 96-bit 난수 IV, AAD로 레코드 정체성을 묶는다 (계획서 §6).
 * IndexedDB에는 바이트 그대로, 백업 파일에는 base64로 저장한다.
 */
export type Envelope = { v: 1; alg: 'A256GCM'; kid: string; iv: Uint8Array; ct: Uint8Array };
export type EnvelopeJson = { v: 1; alg: 'A256GCM'; kid: string; iv: string; ct: string };

export class IntegrityError extends Error {
  constructor(message = '복호화 실패: 데이터가 손상되었거나 키가 다릅니다') {
    super(message);
    this.name = 'IntegrityError';
  }
}

export type DataKey = { key: CryptoKey; kid: string };

export async function seal(dk: DataKey, plaintext: Uint8Array, aad: string): Promise<Envelope> {
  const iv = randomBytes(12);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as BufferSource, additionalData: utf8.encode(aad) as BufferSource, tagLength: 128 },
      dk.key,
      plaintext as BufferSource,
    ),
  );
  return { v: 1, alg: 'A256GCM', kid: dk.kid, iv, ct };
}

export async function open(dk: DataKey, env: Envelope, aad: string): Promise<Uint8Array> {
  if (env.v !== 1 || env.alg !== 'A256GCM') throw new IntegrityError('알 수 없는 암호화 형식');
  if (env.kid !== dk.kid) throw new IntegrityError('다른 키로 암호화된 데이터');
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: env.iv as BufferSource,
          additionalData: utf8.encode(aad) as BufferSource,
          tagLength: 128,
        },
        dk.key,
        env.ct as BufferSource,
      ),
    );
  } catch {
    throw new IntegrityError();
  }
}

export async function sealJson(dk: DataKey, value: unknown, aad: string): Promise<Envelope> {
  return seal(dk, utf8.encode(JSON.stringify(value)), aad);
}

export async function openJson<T = unknown>(dk: DataKey, env: Envelope, aad: string): Promise<T> {
  const bytes = await open(dk, env, aad);
  try {
    return JSON.parse(utf8.decode(bytes)) as T;
  } catch {
    throw new IntegrityError('복호화된 데이터 형식 오류');
  }
}

export const envelopeToJson = (e: Envelope): EnvelopeJson => ({ ...e, iv: toBase64(e.iv), ct: toBase64(e.ct) });
export const envelopeFromJson = (e: EnvelopeJson): Envelope => ({ ...e, iv: fromBase64(e.iv), ct: fromBase64(e.ct) });
